import PAYMENT_OPTIONS from "constants/PAYMENT_OPTIONS";
import TRIANGULATION_OPTIONS from "constants/TRIANGULATION_OPTIONS";
import { Building2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PessoaForm } from "types/PessoaForm";
import { Estagio2Data } from "../ModalRealizarCotacao";
import { Campo, RodapeEtapa, Secao, Selecao } from "./campos";

type Props = {
  previous: () => void;
  next: () => void;
  data: Estagio2Data;
  setData: React.Dispatch<React.SetStateAction<Estagio2Data>>;
};

// Ajuste aqui quais campos de pessoa sao obrigatorios para liberar o avanco
const OBRIGATORIOS: Array<keyof PessoaForm> = [
  "nome",
  "country",
  "city",
  "rua",
  "cep",
  "telefone",
];

const ROTULOS: Record<keyof PessoaForm, string> = {
  nome: "Nome completo",
  telefone: "Telefone",
  country: "País",
  rua: "Endereço",
  city: "Cidade",
  state: "Estado",
  cep: "CEP / ZIP",
};

const Estagio2 = ({ previous, next, data, setData }: Props) => {
  const { t } = useTranslation("dashboard");

  // O remetente quase sempre vem do cadastro e não muda. Começa recolhido num
  // cartão: a tela antiga repetia os dois formulários inteiros lado a lado e o
  // destinatário, que é o que muda a cada envio, disputava espaço com dados
  // que a pessoa já tinha preenchido uma vez nas configurações.
  const [editandoRemetente, setEditandoRemetente] = useState(false);

  const atualizar = (
    quem: "remetente" | "destinatario",
    campo: keyof PessoaForm,
    valor: string,
  ) =>
    setData((prev) => ({ ...prev, [quem]: { ...prev[quem], [campo]: valor } }));

  const pessoaValida = (p: PessoaForm) =>
    OBRIGATORIOS.every((campo) => p[campo]?.toString().trim().length > 0);

  const remetenteOk = pessoaValida(data.remetente);
  const destinatarioOk = pessoaValida(data.destinatario);

  const isValid = useMemo(
    () => remetenteOk && destinatarioOk && Boolean(data.pagamento) && Boolean(data.triangulacao),
    [remetenteOk, destinatarioOk, data.pagamento, data.triangulacao],
  );

  const enderecoRemetente = [
    data.remetente.rua,
    data.remetente.city && `${data.remetente.city}${data.remetente.state ? `, ${data.remetente.state}` : ""}`,
    data.remetente.cep,
  ]
    .filter(Boolean)
    .join(" · ");

  const campoDestinatario = (campo: keyof PessoaForm, placeholder?: string) => (
    <Campo
      label={ROTULOS[campo]}
      valor={data.destinatario[campo] ?? ""}
      aoMudar={(v) => atualizar("destinatario", campo, v)}
      placeholder={placeholder}
      invalido={
        OBRIGATORIOS.includes(campo) && !data.destinatario[campo]?.toString().trim()
      }
    />
  );

  return (
    <>
      <div className="space-y-5">
        {/* REMETENTE */}
        {!editandoRemetente ? (
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-500">
              <Building2 className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {t("modal_make_quotes.sender")}
              </p>
              <p className="truncate text-sm font-semibold text-slate-900">
                {data.remetente.nome || "Remetente não preenchido"}
              </p>
              <p className="truncate text-[11px] text-slate-500">
                {enderecoRemetente || "Endereço não preenchido"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEditandoRemetente(true)}
              className="shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Alterar
            </button>
          </div>
        ) : (
          <Secao
            titulo={t("modal_make_quotes.sender")}
            extra={
              <button
                type="button"
                onClick={() => setEditandoRemetente(false)}
                className="text-[11px] text-slate-500 hover:text-slate-700"
              >
                Recolher
              </button>
            }
          >
            <div className="grid grid-cols-2 gap-4">
              <Campo
                label={ROTULOS.nome}
                valor={data.remetente.nome}
                aoMudar={(v) => atualizar("remetente", "nome", v)}
              />
              <Campo
                label={ROTULOS.telefone}
                valor={data.remetente.telefone}
                aoMudar={(v) => atualizar("remetente", "telefone", v)}
              />
              <Campo
                label={ROTULOS.rua}
                valor={data.remetente.rua}
                aoMudar={(v) => atualizar("remetente", "rua", v)}
              />
              <Campo
                label={ROTULOS.cep}
                valor={data.remetente.cep}
                aoMudar={(v) => atualizar("remetente", "cep", v)}
              />
              <Campo
                label={ROTULOS.city}
                valor={data.remetente.city}
                aoMudar={(v) => atualizar("remetente", "city", v)}
              />
              <div className="grid grid-cols-2 gap-4">
                <Campo
                  label={ROTULOS.state}
                  valor={data.remetente.state}
                  aoMudar={(v) => atualizar("remetente", "state", v)}
                />
                <Campo
                  label={ROTULOS.country}
                  valor={data.remetente.country}
                  aoMudar={(v) => atualizar("remetente", "country", v)}
                  placeholder="BR"
                />
              </div>
            </div>
          </Secao>
        )}

        {/* DESTINATÁRIO */}
        <Secao
          titulo={t("modal_make_quotes.recipient")}
          extra={
            <span className="text-[11px] text-slate-400">
              Confira antes de seguir — vai na etiqueta e na invoice
            </span>
          }
        >
          <div className="grid grid-cols-2 gap-4">
            {campoDestinatario("nome", "Quem recebe")}
            {campoDestinatario("telefone", "+1 305 555 0142")}
            <div className="col-span-2">{campoDestinatario("rua", "Rua, número, complemento")}</div>
            {campoDestinatario("city", "Miami")}
            <div className="grid grid-cols-2 gap-4">
              {campoDestinatario("state", "FL")}
              {campoDestinatario("country", "US")}
            </div>
            {campoDestinatario("cep", "33131")}
          </div>
        </Secao>

        {/* CONDIÇÕES */}
        <Secao titulo="Condições do envio">
          <div className="grid grid-cols-2 gap-4">
            <Selecao
              label={t("fields.payment")}
              valor={data.pagamento}
              aoMudar={(v) => setData((prev) => ({ ...prev, pagamento: v }))}
              opcoes={PAYMENT_OPTIONS}
            />
            <Selecao
              label={t("fields.triangulation")}
              valor={data.triangulacao}
              aoMudar={(v) => setData((prev) => ({ ...prev, triangulacao: v }))}
              opcoes={TRIANGULATION_OPTIONS}
            />
          </div>
        </Secao>
      </div>

      <RodapeEtapa
        onVoltar={previous}
        onAvancar={next}
        avancarLiberado={isValid}
        rotuloAvancar="Ver cotações"
        dica={
          isValid
            ? undefined
            : !remetenteOk
              ? "Complete os dados do remetente"
              : !destinatarioOk
                ? "Complete os dados do destinatário"
                : "Escolha pagamento e triangulação"
        }
      />
    </>
  );
};

export default Estagio2;
